<script lang="ts" setup>
import { TransportSelector } from '@apps/main/new-desktop/modules/tranposrtSelector';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { i18n } from '@apps/main/modules/localization/localizationSetup';

import { AppButton, SemboLineIllustrationBanner } from '@libs/ui';
import { TransportType } from '@libs/trip-planner-interfaces';

interface TransportBannerProps {
  country?: string;
}

const props = defineProps<TransportBannerProps>();

interface TransportBannerEmits {
  (eventName: 'add-flight'): void;
  (eventName: 'add-ferry'): void;
  (eventName: 'select-transport', value: TransportType): void;
}
const emit = defineEmits<TransportBannerEmits>();

const statsigEventsStore = useStatsigEventsStore();
const browserStore = useBrowserStore();

const { t } = i18n.global;
</script>

<template>
  <div class="transport-banner">
    <span class="banner-message">
      {{
        props.country
          ? t('planner.transport.ready_for_transport_dynamic', [props.country])
          : t('planner.transport.ready_for_transport_static')
      }}
    </span>
    <div class="button-container">
      <AppButton
        type="secondary"
        state="enabled"
        size="small"
        @click="emit('add-flight')"
      >
        {{ $t('planner.transport.add_flight') }}
      </AppButton>
      <AppButton
        type="secondary"
        state="enabled"
        size="small"
        @click="emit('add-ferry')"
      >
        {{ $t('planner.transport.add_ferry') }}
      </AppButton>

      <div class="button-content">
        <TransportSelector
          :transport-type="TransportType.None"
          :initial-option="
            browserStore.isSmallerThan1200px
              ? t('planner.transport.other_transports_small')
              : t('planner.transport.see_all_transports')
          "
          :disabled="false"
          is-upsell-no-transport
          is-transport-upsell-banner
          @select="emit('select-transport', $event)"
          @open="statsigEventsStore.logTripOverviewTransportSelectorOpened"
        />
      </div>
    </div>
    <SemboLineIllustrationBanner class="sembo-illustration" />
  </div>
</template>

<style lang="scss" scoped>
.transport-banner {
  position: relative;
  display: grid;
  grid-template-rows: auto;
  grid-template-columns: 1fr auto;
  align-items: center;
  background-color: var(--color-primary-90);
  margin: 0 var(--spacing-24) var(--spacing-32) var(--spacing-24);
  border-radius: var(--spacing-8);
  padding: var(--spacing-24);
  overflow: hidden;

  @include for-less-or-equal-than-900-px {
    padding: var(--spacing-16) var(--spacing-12);
    gap: var(--spacing-16);
    margin: 0 var(--spacing-12) var(--spacing-32) var(--spacing-12);
    grid-template-rows: auto auto;
    grid-template-columns: auto;
  }
  .banner-message {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    flex: 1;
    @include font-label-xxl-bold;
    font-size: var(--font-size-18);
    z-index: var(--notification-z-index);
    @include for-less-or-equal-than-900-px {
      @include font-label-l-bold;
      width: 100%;
      gap: var(--spacing-16);
    }
  }

  .button-container {
    display: flex;
    gap: var(--spacing-16);
    z-index: var(--notification-z-index);
    flex-wrap: wrap;
    @include for-less-or-equal-than-900-px {
      column-gap: var(--spacing-16);
      row-gap: var(--spacing-4);
    }
    .button-content {
      display: flex;
      align-items: center;
      gap: var(--spacing-8);
    }
  }
  .sembo-illustration {
    position: absolute;
    left: 450px;
    bottom: 0;
    @include for-less-or-equal-than-900-px {
      left: -40px;
      bottom: -20px;
      transform: rotateZ(15deg);
    }
  }
  &::after {
    content: '';
    height: 35px;
    width: 0px;
    border-right: 1px dashed var(--color-secondary-50);
    position: absolute;
    bottom: -35px;
    left: 24px;
  }
}
</style>
